import React from 'react';
import { observer } from 'mobx-react';
import { Box, Fade } from '@material-ui/core';
import { useReportContext } from 'contexts/report/ReportContext';
import reportLocales from 'globals/locales/report';
import { getCombinedStatus, isSuccessState, wait } from 'globals/utils';
import { TransactionType } from 'globals/types';
import FsPaper from 'ui-elements/main-elements/FsPaper';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import FsTransactionRowsWithBarGraph from 'ui-elements/tables/FsTransactionRowsWithBarGraph';
import { getTooltipContent } from 'ui-elements/modals/FsDefaultDialog/FsDefaultDialog.utils';
import FsInnerTabPanel from 'ui-elements/tabs/FsInnerTabPanel';
import FsGridContainer from 'ui-elements/grid/FsGridContainer/FsGridContainer';
import FsGridItem from 'ui-elements/grid/FsGridItem/FsGridItem';
import { tooltipContent } from './ReportTopRecurringTransactions.utils';

// Locales
const { link, title, tabLabels } = reportLocales.topRecurringTransactions;

const ReportTopRecurringTransactions: React.FC = () => {
  const {
    report: {
      topRecurringExpenditures: {
        status: topRecurringExpendituresStatus,
        rows: expenditureRows,
      },
      topRecurringIncomes: {
        status: topRecurringIncomesStatus,
        rows: incomeRows,
      },
    },
  } = useReportContext();

  const combinedStatus = getCombinedStatus([
    topRecurringExpendituresStatus,
    topRecurringIncomesStatus,
  ]);
  const [tabIdx, setTabIdx] = React.useState<number>(0);
  const [blockIdx, setBlockIdx] = React.useState<number>(0);
  const [tabChanging, setTabChanging] = React.useState<boolean>(false);

  const handleChange = async (
    event: React.ChangeEvent<Record<string, unknown>>,
    newTabIdx: number
  ): Promise<void> => {
    setTabChanging(true);
    setTabIdx(newTabIdx);
    await wait(300);
    setBlockIdx(newTabIdx);
    setTabChanging(false);
  };

  const isBlockIdx = (idx: number): boolean => {
    return idx === blockIdx;
  };

  if (!isSuccessState(combinedStatus))
    return (
      <FsDefaultFallbackComponent
        status={combinedStatus}
        data-test="default-fallback-component"
      />
    );

  return (
    <FsGridContainer>
      <FsGridItem xs={12}>
        <FsPaper
          title={title}
          link={link}
          tooltipContent={getTooltipContent(tooltipContent)}
          data-test="report-top-recurring-transactions-component"
        >
          <FsInnerTabPanel
            labels={tabLabels}
            onChange={handleChange}
            value={tabIdx}
          />
          <Fade in={!tabChanging}>
            <Box>
              {isBlockIdx(0) && (
                <FsTransactionRowsWithBarGraph
                  rows={incomeRows()}
                  type={TransactionType.TOP_INCOME}
                />
              )}
              {isBlockIdx(1) && (
                <FsTransactionRowsWithBarGraph
                  rows={expenditureRows()}
                  type={TransactionType.TOP_EXPENDITURE}
                />
              )}
            </Box>
          </Fade>
        </FsPaper>
      </FsGridItem>
    </FsGridContainer>
  );
};

ReportTopRecurringTransactions.displayName = 'ReportTopRecurringTransactions';

export default observer(ReportTopRecurringTransactions);
